import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { LayoutGrid, List, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { API_URL } from '../config/api';
import { Circle, CircleDetails, Member, Filters, emptyFilters, fetchCircles, sizeLabel } from './circles/model';
import CircleList from './circles/CircleList';
import CircleDetail from './circles/CircleDetail';
import { CircleFilters, CircleConfirm } from './circles/CircleDialogs';
import { utcTime } from './users/model';
import './users/users.css';
import './users/brand.css';
export default function Families() {
  const [circles, setCircles] = useState<Circle[]>([]);
  const [loading, setLoading] = useState(true);
  const [partial, setPartial] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [cards, setCards] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Circle | null>(null);
  const [detail, setDetail] = useState<CircleDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [pending, setPending] = useState<{
    circle: Circle;
    member?: Member;
  } | null>(null);
  const requestId = useRef(0);
  const alive = useRef(true);
  const listBusy = useRef(false);
  const fetchList = async () => {
    if (listBusy.current) return;
    listBusy.current = true;
    setLoading(true);
    setError('');
    try {
      const result = await fetchCircles();
      if (alive.current) {
        setCircles(result.circles);
        setPartial(result.partial);
      }
    } catch {
      if (alive.current) setError('Çevreler yüklenemedi. Önceki liste korunuyor.');
    } finally {
      listBusy.current = false;
      if (alive.current) setLoading(false);
    }
  };
  useEffect(() => {
    alive.current = true;
    document.body.classList.add('users-brand');
    fetchList();
    return () => {
      alive.current = false;
      requestId.current += 1;
      document.body.classList.remove('users-brand');
    };
  }, []);
  const loadDetail = async (circle: Circle) => {
    const id = ++requestId.current;
    setSelected(circle);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const response = await axios.get<CircleDetails>(`${API_URL}/admin/families/${circle.id}`);
      if (alive.current && id === requestId.current) setDetail(response.data);
    } catch {
      if (alive.current && id === requestId.current) setDetailError('Çevre detayları yüklenemedi.');
    } finally {
      if (alive.current && id === requestId.current) setDetailLoading(false);
    }
  };
  const closeDetail = () => {
    requestId.current += 1;
    setSelected(null);
    setDetail(null);
    setDetailLoading(false);
  };
  const filtered = useMemo(() => circles.filter(circle => {
    const query = [circle.id, circle.name, circle.description, circle.owner_name, circle.owner_id].join(' ').toLowerCase();
    const created = utcTime(circle.created_at);
    return query.includes(search.toLowerCase()) && (!filters.size || sizeLabel(circle.member_count) === filters.size) && (!filters.from || created >= Date.parse(filters.from + 'T00:00:00Z')) && (!filters.to || created < Date.parse(filters.to + 'T00:00:00Z') + 86400000);
  }), [circles, search, filters]);
  useEffect(() => {
    setPage(1);
  }, [search, filters]);
  useEffect(() => {
    setPage(current => Math.min(current, Math.max(1, Math.ceil(filtered.length / 25))));
  }, [filtered.length]);
  const submit = async () => {
    if (!pending) return;
    const {
      circle,
      member
    } = pending;
    if (member) {
      if (member.id === (detail?.owner_id ?? circle.owner_id)) throw new Error('Çevre sahibi çıkarılamaz');
      await axios.delete(`${API_URL}/admin/families/${circle.id}/members/${member.id}`);
      setCircles(current => current.map(item => item.id === circle.id ? {
        ...item,
        member_count: Math.max(0, item.member_count - 1)
      } : item));
      setSelected(current => current?.id === circle.id ? {
        ...current,
        member_count: Math.max(0, current.member_count - 1)
      } : current);
      setDetail(current => current?.id === circle.id ? {
        ...current,
        members: current.members.filter(item => item.id !== member.id),
        member_count: Math.max(0, current.member_count - 1)
      } : current);
      setNotice(`${member.full_name || member.username}, ${circle.name} çevresinden çıkarıldı.`);
    } else {
      await axios.delete(`${API_URL}/admin/families/${circle.id}`);
      setCircles(current => current.filter(item => item.id !== circle.id));
      closeDetail();
      setNotice(`${circle.name} çevresi silindi.`);
    }
  };
  const stats = [['Yüklenen çevre', circles.length], ['Küçük · 0‑2', circles.filter(circle => circle.member_count <= 2).length], ['Orta · 3‑5', circles.filter(circle => circle.member_count > 2 && circle.member_count <= 5).length], ['Büyük · 6+', circles.filter(circle => circle.member_count > 5).length]] as const;
  const activeFilters = Number(!!filters.size) + Number(!!filters.from) + Number(!!filters.to);
  return <div className="users-redesign space-y-5">
    <header className="flex items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Çevreler</h1>
        <p className="hidden sm:block text-sm text-gray-500 mt-2">Çevre sahiplerini, üyeleri ve oluşturulma bilgilerini yönetin.</p>
      </div>
      <button className="user-btn" disabled={loading} onClick={fetchList} aria-label="Çevre listesini yenile">
        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        <span className="hidden sm:inline">Yenile</span>
      </button>
    </header>
    <div className="management-stats grid grid-cols-4 gap-2 sm:gap-3">
      {stats.map(([label, value], index) => <div key={label} data-tone={['brand', 'positive', 'blue', 'brand'][index]} className="management-stat bg-white border border-gray-100 rounded-2xl p-2.5 sm:p-4">
        <p className="text-[10px] sm:text-xs text-gray-500">
          {label}
        </p>
        <p className="text-xl sm:text-2xl font-semibold mt-1">
          {loading && !circles.length ? '—' : value.toLocaleString('tr-TR')}
        </p>
      </div>)}
    </div>
    {error && <p role="alert" className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">
      {error}
      <button onClick={fetchList} className="underline ml-2">Yeniden dene</button>
    </p>}
    {partial && <p role="status" className="bg-amber-50 text-amber-800 p-4 rounded-xl text-sm">Kısmi liste: 100.000 kayıt güvenlik sınırına ulaşıldı. Sayaçlar yalnız yüklenen çevreleri gösterir.</p>}
    {notice && <p role="status" className="bg-lifeshare-50 text-lifeshare-700 p-3 rounded-xl text-sm flex justify-between gap-3">
      {notice}
      <button aria-label="Bildirimi kapat" onClick={() => setNotice('')}>
        <X size={16} />
      </button>
    </p>}
    <section className="management-toolbar bg-white border border-gray-100 rounded-2xl p-4 space-y-3" aria-label="Çevre arama ve filtreler">
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[190px]">
          <Search className="absolute left-3 top-3.5 text-gray-400" size={17} />
          <input
            className="management-search user-input pl-10"
            aria-label="Çevrelerde ara"
            placeholder="Çevre, sahip veya kimlik ara…"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <button data-testid="circle-filters" className="user-btn" onClick={() => setShowFilters(true)}>
          <SlidersHorizontal size={16} />
          Filtreler
          {activeFilters ? ` (${activeFilters})` : ''}
        </button>
        <div className="management-view-toggle hidden sm:flex border border-gray-200 rounded-xl p-1">
          <button
            className={`p-2 rounded-lg ${!cards ? 'text-lifeshare-600 bg-lifeshare-50' : 'text-gray-400'}`}
            aria-label="Liste görünümü"
            aria-pressed={!cards}
            onClick={() => setCards(false)}
          >
            <List size={17} />
          </button>
          <button
            className={`p-2 rounded-lg ${cards ? 'text-lifeshare-600 bg-lifeshare-50' : 'text-gray-400'}`}
            aria-label="Kart görünümü"
            aria-pressed={cards}
            onClick={() => setCards(true)}
          >
            <LayoutGrid size={17} />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {`${filtered.length} sonuç · ${circles.length} yüklenen çevre`}
        </span>
        {activeFilters > 0 && <button className="text-lifeshare-600 min-h-[44px]" onClick={() => setFilters(emptyFilters)}>Filtreleri temizle</button>}
      </div>
    </section>
    {loading && !circles.length ? <p role="status" className="text-center p-12 text-gray-500">Çevreler yükleniyor…</p> : <CircleList circles={filtered.slice((page - 1) * 25, page * 25)} open={loadDetail} cards={cards} />}
    <footer className="flex justify-between items-center gap-3 text-xs text-gray-500 pb-4">
      <span>
        {filtered.length ? `${(page - 1) * 25 + 1}–${Math.min(page * 25, filtered.length)} / ${filtered.length}` : '0 çevre'}
      </span>
      <div className="flex gap-2">
        <button className="user-btn" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Önceki</button>
        <button className="user-btn" disabled={page * 25 >= filtered.length} onClick={() => setPage(value => value + 1)}>Sonraki</button>
      </div>
    </footer>
    {selected && <CircleDetail
      key={selected.id}
      summary={selected}
      detail={detail}
      loading={detailLoading}
      error={detailError}
      retry={() => loadDetail(selected)}
      close={closeDetail}
      remove={member => setPending({
      circle: selected,
      member
    })}
      deleteCircle={() => setPending({
      circle: selected
    })}
    />}
    {showFilters && <CircleFilters value={filters} set={setFilters} close={() => setShowFilters(false)} />}
    {pending && <CircleConfirm circle={pending.circle} member={pending.member} close={() => setPending(null)} submit={submit} />}
  </div>;
}
